/** Azure Static Web Apps built-in auth helpers (cookie ~8h, renewed via platform endpoints). */

export const AUTH_REFRESH_MS = 30 * 60 * 1000;

const APP_ROLES = ['member', 'admin'];
const SSO_ATTEMPT_KEY = 'family-agenda-sso-attempt';
const SSO_RETRY_MS = 60 * 1000;

export function isInstalledPwa() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches
    || window.matchMedia('(display-mode: fullscreen)').matches
    || window.navigator.standalone === true
  );
}

export function buildAadLoginUrl(returnPath = '/') {
  const apiBase = process.env.REACT_APP_API_URL || '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const returnUrl = encodeURIComponent(`${origin}${returnPath}`);
  return `${apiBase}/.auth/login/aad?post_login_redirect_uri=${returnUrl}`;
}

export function buildLogoutUrl(returnPath = '/login') {
  const apiBase = process.env.REACT_APP_API_URL || '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const returnUrl = encodeURIComponent(`${origin}${returnPath}`);
  return `${apiBase}/.auth/logout?post_logout_redirect_uri=${returnUrl}`;
}

/** Normalize SWA /.auth/me JSON (object, array, or null) into a client principal or null. */
export function parseAuthMePayload(data) {
  if (data == null) return null;
  if (Array.isArray(data)) {
    if (data.length === 0) return null;
    const entry = data.find((item) => item?.clientPrincipal) ?? data[0];
    return parseAuthMePayload(entry);
  }
  if (typeof data === 'object' && 'clientPrincipal' in data) {
    return data.clientPrincipal ?? null;
  }
  if (data.userId || data.identityProvider || data.userDetails) {
    return data;
  }
  return null;
}

export function getPrincipalEmail(principal) {
  if (!principal) return null;
  const direct = principal.userDetails ?? principal.email ?? null;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  const claims = Array.isArray(principal.claims) ? principal.claims : [];
  for (const claim of claims) {
    const typ = String(claim?.typ || '').toLowerCase();
    if (typ === 'email' || typ === 'preferred_username' || typ === 'emails') {
      const val = claim?.val;
      if (typeof val === 'string' && val.trim()) return val.trim();
    }
  }
  return null;
}

export function isAuthenticatedPrincipal(principal) {
  return Boolean(
    principal?.userId || principal?.userDetails || principal?.identityProvider
  );
}

export async function getAuthPrincipal() {
  try {
    const res = await fetch('/.auth/me', { credentials: 'include', cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    return parseAuthMePayload(data);
  } catch {
    return null;
  }
}

export function principalHasAppAccess(principal) {
  if (!principal) return false;
  const roles = Array.isArray(principal.userRoles)
    ? principal.userRoles.map((role) => String(role).toLowerCase())
    : [];
  return roles.some((role) => APP_ROLES.includes(role));
}

export async function isAuthenticated() {
  const principal = await getAuthPrincipal();
  return isAuthenticatedPrincipal(principal);
}

/** Best-effort sliding renewal while the SWA session cookie is still valid. */
export async function renewSwaSession() {
  const opts = { credentials: 'include', cache: 'no-store' };
  await fetch('/.auth/complete', opts).catch(() => {});
  await fetch('/.auth/refresh', opts).catch(() => {});
}

export function shouldAttemptColdBootSso() {
  if (!isInstalledPwa()) return false;
  const lastAttempt = Number(sessionStorage.getItem(SSO_ATTEMPT_KEY) || 0);
  return !lastAttempt || Date.now() - lastAttempt > SSO_RETRY_MS;
}

export function markColdBootSsoAttempt() {
  sessionStorage.setItem(SSO_ATTEMPT_KEY, String(Date.now()));
}

export function clearColdBootSsoAttempt() {
  sessionStorage.removeItem(SSO_ATTEMPT_KEY);
}

export async function registerBackgroundAuthRenewal(registration) {
  if (!registration) return;

  try {
    if ('periodicSync' in registration) {
      await registration.periodicSync.register('swa-auth-renew', {
        minInterval: 2 * 60 * 60 * 1000,
      });
    }
  } catch {
    // Unsupported or permission denied — normal on many tablets.
  }

  try {
    if ('sync' in registration) {
      await registration.sync.register('swa-auth-renew');
    }
  } catch {
    // ignore
  }
}

export function scheduleBackgroundAuthRenewal(registration) {
  if (!registration?.active) return;
  registration.active.postMessage({ type: 'schedule-auth-renew' });
}
